var _react_refresh_temp_1;
var _react_refresh_temp_2;
_react_refresh_temp_2 = $RefreshSig$();
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useKeyboardHandler } from '#hooks';
import { C } from '#integrations';
import { getDescriptionId, getLabelId } from '#services';
import { Box } from '../Box';
import { IconButton } from '../buttons';
import { CheckIcon, InfoCircleIcon, XCloseIcon } from '../icons';
import { DescribedByContext } from '../inputs/components/DescribedByContext';
import { Tooltip, useTooltip } from '../Tooltip';
import { Typography } from '../Typography';
import { EditableValue } from './components';
import { InlineFormTestIdFactory } from './interactions';
import styles from './InlineForm.module.css';
export const InlineForm = ({ cancelAriaLabel, 'data-testid': testid, description, disabled, formElement, inputId, isFullWidth = false, isLoading, isSaveAllowed, isSuccess, label, labelDefault, labelTooltip, onActivate, onCancel, onSave, placeholder, resetToDefault, saveAriaLabel, tooltipPosition, trigger, value, valueClassName, width = 'sm', }) => {
    _react_refresh_temp_2();
    const [isActive, setIsActive] = useState(false);
    const { anchorRef, isTooltipActive, tooltipProps, triggerProps } = useTooltip({
        placement: tooltipPosition,
    });
    const handleSave = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!isSaveAllowed) {
            return;
        }
        onSave();
        if (isSuccess) {
            setIsActive(false);
        }
    }, [isSaveAllowed, onSave, isSuccess]);
    const handleActivate = useCallback((event) => {
        // This is needed, otherwise the event will bubble up to the document and
        // close the inline form immediately after opening it through the useEffect below
        // which attaches a document click listener.
        event.preventDefault();
        event.stopPropagation();
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- intentional ternary/short-circuit statement (allowed on master; v8 newly added this rule)
        onActivate ? onActivate(event) : setIsActive(true);
    }, [onActivate]);
    const handleCancel = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        if (onCancel) {
            onCancel();
        }
        setIsActive(false);
    }, [onCancel]);
    // Only while editing, so Escape still reaches e.g. the Reset to default tooltip
    const { handleKeyboardEvent } = useKeyboardHandler({
        ...(isActive && {
            onEnter: (event) => {
                // Ignore auto-repeat keydowns so holding Enter to open the editor
                // cannot immediately save; a save requires a fresh, deliberate press.
                if (event.repeat) {
                    return;
                }
                handleSave(event);
            },
            onEscape: handleCancel,
        }),
        preventDefault: false,
        stopPropagation: true,
    });
    const containerRef = useRef(null);
    const isMouseDownInsideRef = useRef(false);
    useEffect(() => {
        const isInsideContainer = (target) => Boolean(containerRef.current?.contains(target));
        const onDocumentMouseDownHandler = (event) => {
            isMouseDownInsideRef.current = isInsideContainer(event.target);
        };
        const onDocumentClickHandler = (event) => {
            if (!isActive) {
                return;
            }
            // A drag that starts inside (e.g. selecting the input text) but ends outside
            // still fires a click on the common ancestor - that must not close the form.
            // `detail` is 0 for keyboard and programmatic clicks, which have no mouse down
            // of their own, so the recorded origin is stale for them and must be ignored.
            const isDragFromInside = event.detail > 0 && isMouseDownInsideRef.current;
            if (isDragFromInside || isInsideContainer(event.target)) {
                return;
            }
            setIsActive(false);
        };
        document.addEventListener('mousedown', onDocumentMouseDownHandler, true);
        document.addEventListener('click', onDocumentClickHandler);
        return () => {
            document.removeEventListener('mousedown', onDocumentMouseDownHandler, true);
            document.removeEventListener('click', onDocumentClickHandler);
        };
    }, [isActive]);
    useEffect(() => {
        if (isSuccess) {
            setIsActive(false);
        }
    }, [isSuccess]);
    return (_jsxs("div", { className: styles.Container, ref: containerRef, children: [Boolean(label) && (_jsxs("span", { className: styles.LabelContainer, ...(labelTooltip && triggerProps), children: [_jsx(Typography, { className: C(disabled && styles.LabelDisabled), color: "accent", "data-testid": InlineFormTestIdFactory.getInlineFormLabel(testid), 
                        /**
                         * The `id` used for the label must match the `id` of the form element for accessibility.
                         */
                        htmlFor: isActive ? inputId : undefined, id: getLabelId(inputId), kind: "label", children: label }), labelTooltip && !disabled && (_jsxs(_Fragment, { children: [_jsx(InfoCircleIcon, { ref: anchorRef, size: "small" }), isTooltipActive && (_jsx(Tooltip, { contents: labelTooltip, tooltipProps: tooltipProps }))] }))] })), Boolean(description) && (_jsx(Typography, { className: disabled ? styles.DescriptionDisabled : undefined, "data-testid": InlineFormTestIdFactory.getInlineFormDescription(testid), id: getDescriptionId(inputId), kind: "generic-small", children: description })), _jsxs("div", { className: styles.InlineEditorContainer, onKeyDown: handleKeyboardEvent, children: [_jsx(Box, { ...(isFullWidth ? { isFullWidth } : { width }), ...(!isActive && { gap: 's', padding: '0 0 xs 0' }), children: isActive ? (_jsx(DescribedByContext.Provider, { value: description
                                ? getDescriptionId(inputId)
                                : undefined, children: typeof formElement === 'function'
                                ? formElement({
                                    'aria-labelledby': getLabelId(inputId),
                                })
                                : formElement })) : trigger ? (trigger({
                            onActivate: handleActivate,
                        })) : (_jsx(EditableValue, { "data-testid": testid, disabled: disabled ?? false, labelDefault: labelDefault, onActivate: handleActivate, placeholder: placeholder, resetToDefault: resetToDefault, value: value, valueClassName: valueClassName })) }), isActive && (_jsxs(Box, { gap: "s", padding: "0 0 xs 0", shrink: 0, children: [_jsx(IconButton, { "aria-label": saveAriaLabel, "data-testid": InlineFormTestIdFactory.getInlineFormSaveButton(testid), disabled: !isSaveAllowed, Icon: CheckIcon, isLoading: isLoading ?? false, onClick: handleSave, size: "big" }), _jsx(IconButton, { "aria-label": cancelAriaLabel, "data-testid": InlineFormTestIdFactory.getInlineFormCancelButton(testid), Icon: XCloseIcon, kind: "secondary", onClick: handleCancel, size: "big" })] }))] })] }));
};
_react_refresh_temp_1 = InlineForm;
$RefreshReg$(_react_refresh_temp_1, "InlineForm");
_react_refresh_temp_2(InlineForm, "rLFZSQnGqb0fn9K656hzRM2bj9Y=", false, () => [useTooltip, useKeyboardHandler]);
